/**
 * ePay Payment Service
 * Безопасная интеграция с Halyk Bank ePay 2.0
 *
 * ⚠️ ВАЖНО: ClientSecret должен храниться ТОЛЬКО на сервере!
 * Этот файл использует backend API для получения токена
 */

import axios from 'axios';

const EPAY_CONFIG = {
    // Публичные данные - можно в клиенте
    terminalId: process.env.REACT_APP_EPAY_TERMINAL_ID,
    clientId: process.env.REACT_APP_EPAY_CLIENT_ID,

    // URL endpoints
    // Production: https://epay-oauth.homebank.kz/oauth2/token
    // Widget: https://epay.homebank.kz/payform/payment-api.js
    oauthUrl: 'https://epay-oauth.homebank.kz/oauth2/token',
    widgetUrl: 'https://epay.homebank.kz/payform/payment-api.js',

    // Ваш backend API endpoint (нужно создать)
    backendApiUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000/api'
};

/**
 * Загружает ePay виджет скрипт
 */
export const loadEPayWidget = () => {
    return new Promise((resolve, reject) => {
        // Проверяем, не загружен ли уже
        if (window.halyk && window.halyk.showPaymentWidget) {
            resolve(window.halyk);
            return;
        }

        const script = document.createElement('script');
        script.src = EPAY_CONFIG.widgetUrl;
        script.async = true;

        script.onload = () => {
            if (window.halyk && window.halyk.showPaymentWidget) {
                resolve(window.halyk);
            } else {
                reject(new Error('ePay виджет не загрузился корректно'));
            }
        };

        script.onerror = () => {
            reject(new Error('Не удалось загрузить ePay виджет'));
        };

        document.body.appendChild(script);
    });
};

/**
 * Получает access token через backend API
 * ⚠️ НЕ делайте это на клиенте! ClientSecret должен быть на сервере!
 */
export const getEPayToken = async (invoiceId, amount) => {
    try {
        // Запрос к вашему backend API
        const response = await axios.post(`${EPAY_CONFIG.backendApiUrl}/getEPayToken`, {
            invoiceId,
            amount,
            currency: 'KZT',
            terminal: EPAY_CONFIG.terminalId
        });

        if (!response.data || !response.data.access_token) {
            throw new Error('Не удалось получить токен авторизации');
        }

        return response.data;
    } catch (error) {
        console.error('Ошибка получения ePay токена:', error);
        throw new Error('Ошибка авторизации платежа. Попробуйте позже.');
    }
};

/**
 * Создает платеж через ePay с прямой переадресацией
 * (без iframe, решает проблему Permissions Policy)
 */
export const createPayment = async (paymentData) => {
    const {
        amount,
        description,
        accountId,
        customerName,
        customerEmail,
        invoiceId,
        successUrl,
        failureUrl
    } = paymentData;

    try {
        // 1. Получаем токен через backend
        const authData = await getEPayToken(invoiceId, amount);

        // 2. Формируем объект платежа
        const paymentObject = {
            invoiceId: invoiceId,
            // invoiceIdAlt опциональный, можно не передавать или дублировать invoiceId
            // Redirect URLs - куда вернется пользователь после оплаты
            backLink: successUrl || `https://eparcel-kz.web.app/PaymentSuccess?invoice=${invoiceId}&amount=${amount}&userId=${accountId}`,
            failureBackLink: failureUrl || `https://eparcel-kz.web.app/PaymentSuccess?status=failed&invoice=${invoiceId}`,
            // Server callbacks - для получения статуса платежа на backend
            postLink: `${EPAY_CONFIG.backendApiUrl}/ePayCallback`,
            failurePostLink: `${EPAY_CONFIG.backendApiUrl}/ePayCallbackFailure`,
            language: 'rus',
            description: description || 'Оплата услуг Eparcel',
            accountId: accountId,
            terminal: EPAY_CONFIG.terminalId,
            amount: amount,
            currency: 'KZT',
            auth: authData
        };

        console.log('Payment redirect with invoice:', invoiceId);

        // 3. Используем метод pay() из виджета для редиректа
        await loadEPayWidget();

        // Вызываем метод pay который делает location.href
        window.halyk.pay(paymentObject);

        // Возвращаем промис для совместимости
        return new Promise((resolve) => {
            resolve({
                success: true,
                redirecting: true,
                message: 'Переадресация на страницу оплаты Halyk Bank'
            });
        });

    } catch (error) {
        console.error('Ошибка создания платежа:', error);
        throw error;
    }
};

/**
 * Проверяет статус платежа
 */
export const checkPaymentStatus = async (invoiceId) => {
    try {
        const response = await axios.get(
            `${EPAY_CONFIG.backendApiUrl}/checkPaymentStatus?invoiceId=${invoiceId}`
        );
        return response.data;
    } catch (error) {
        console.error('Ошибка проверки статуса платежа:', error);
        throw error;
    }
};

/**
 * Генерирует уникальный ID для платежа
 * Halyk Bank требует: только цифры, 6-15 символов
 */
export const generateInvoiceId = () => {
    // Date.now() дает 13 цифр (например: 1729094556242)
    // Добавляем 2 случайные цифры для уникальности = 15 цифр (максимум для Halyk)
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 100); // 0-99
    return `${timestamp}${random.toString().padStart(2, '0')}`; // Всегда 15 цифр
};

export default {
    loadEPayWidget,
    createPayment,
    checkPaymentStatus,
    generateInvoiceId,
    EPAY_CONFIG
};
